import axios from 'axios';
import { API_REFRESH_TOKEN } from 'config/data';
import { IDENTITY_STATUS } from 'utils/data';
import { endSession } from 'utils/helper';
import { getAuthenticator, getRefreshToken, setAuthenticator, setRefreshToken } from 'utils/localStorage';

interface FailedQueueItem {
  resolve: (value?: unknown) => void;
  reject: (reason?: any) => void;
}


const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_BASE_ENDPOINT_URL,
});

axiosInstance.interceptors.request.use(
  (config) => {
    // Lấy token từ localStorage hoặc nơi lưu trữ khác
    const token = getAuthenticator();

    if (token) {
      // Thêm token vào header Authorization
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);


let isRefreshing = false;
let failedQueue: FailedQueueItem[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};


axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response && error.response.status === 401) {
      if (!originalRequest._retry) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers['Authorization'] = `Bearer ${token}`;
              return axiosInstance(originalRequest);
            })
            .catch((err) => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        return new Promise((resolve, reject) => {
          const refreshToken = getRefreshToken();
          const data = { refreshToken };

          axios.post(API_REFRESH_TOKEN, data)
            .then(({ data }) => {
              if (data.status === IDENTITY_STATUS.SUCCESS && data?.data !== null) {
                const newAccessToken = data.data.accessToken;
                setAuthenticator(newAccessToken);
                setRefreshToken(data.data.refreshToken);
                axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`;
                originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
                processQueue(null, newAccessToken);
                resolve(axiosInstance(originalRequest));
              } else {
                endSession();
                processQueue(new Error('Invalid refresh token'), null);
                reject(new Error('Invalid refresh token'));
              }
            })
            .catch((err) => {
              processQueue(err, null);
              endSession();
              reject(err);
            })
            .finally(() => {
              isRefreshing = false;
            });
        });
      }
    }
    return Promise.reject(error);
  },
);

export default axiosInstance;
